import { useQuery } from "react-query";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import z from "zod";
// Remove EntityTag and TagValue imports - legacy system no longer used
import { isQueryReady } from "src/api/axiosClient/utils";

export const getOneOrganizationKey = "getOneCompany" as const;

type Options = {
    companyId?: string;
    organizationId?: number;
    enabled?: boolean;
    withoutRelations?: boolean;
    extraQueryKey?: string;
};

export const useGetOneOrganization = (options: Options) => {
    const { companyId, organizationId, enabled = true, extraQueryKey, withoutRelations = false } = options;

    const isEnabled = companyId !== undefined && enabled;
    const endpoint =
        organizationId === undefined ?
            `/api/organizations/${companyId}`
        :   `/api/organizations/${organizationId}/suppliers/${companyId}`;
    const endpointPattern = organizationId === undefined ? "/organizations/:id" : "/organizations/:orgId/suppliers/:id";
    const query = useQuery({
        queryKey: [getOneOrganizationKey, companyId, organizationId, extraQueryKey, withoutRelations],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: async () => {
            const params = {
                withoutRelations,
            };

            const response = await loggedInApiCall.get(endpoint, { params });
            return response;
        },
        select: (request) => {
            const company = request.data;
            const data = zodParseWithDetailedLog(company, CompanySchema, {
                operation: "GET",
                endpoint: endpointPattern,
            });
            return data;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        company: query.data,
        isReady,
        ...query,
    };
};

export const ContributorZodSchema: z.ZodSchema<Contributor> = z.object({
    "@id": z.string(),
    id: z.number(),
    email: z.string(),
    firstname: z.string(),
    lastname: z.string(),
});

export type Contributor = {
    "@id": IRI;
    id: number;
    email: string;
    firstname: string;
    lastname: string;
};

export const CompanySectorZodSchema: z.ZodSchema<CompanySector> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    ssqId: z
        .number()
        .nullish()
        .transform((value) => value ?? undefined),
    ssqs: z
        .array(
            z.object({
                ssqId: z.number(),
                position: z.number().optional(),
                headings: z
                    .array(
                        z.object({
                            hidden: z.boolean().optional(),
                            title: z.string(),
                        }),
                    )
                    .optional(),
            }),
        )
        .optional(),
    variationIds: z.number().array().optional(),
    defaultVariationIds: z.number().array().optional(),
});

type CompanySector = {
    "@id": string;
    id: number;
    name?: string;
    ssqId?: number;
    ssqs?: Array<{
        ssqId: number;
        position?: number;
        headings?: Array<{
            hidden?: boolean;
            title: string;
        }>;
    }>;
    variationIds?: number[];
    defaultVariationIds?: number[];
};

const IngredientLineZodSchema: z.ZodSchema<IngredientSupplierIngredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type IngredientLine = {
    "@id": IRI;
    id: number;
    name: string;
};

const IngredientSupplierIngredientZodSchema: z.ZodSchema<IngredientSupplierIngredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    ingredientLine: IngredientLineZodSchema.optional(),
    ingredientReferenceId: z.number().optional(),
    referenceSource: z.union([z.literal("sure"), z.literal("foodpilot")]).optional(),
    origin: z.string().optional(),
});

export type IngredientSupplierIngredient = {
    "@id": IRI;
    id: number;
    name: string;
    ingredientLine?: IngredientLine;
    ingredientReferenceId?: number;
    referenceSource?: "sure" | "foodpilot";
    origin?: string;
};

const IngredientSupplierZodSchema: z.ZodSchema<IngredientSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    ingredient: IngredientSupplierIngredientZodSchema,
    managedBy: z.union([z.string(), z.object({ "@id": z.string() })]).optional(),
});

type IngredientSupplier = {
    "@id": IRI;
    id: number;
    ingredient: IngredientSupplierIngredient;
    managedBy?: IRI | { "@id": IRI };
};

const CompanyBaseSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    isProvider: z.boolean().optional(),
    isPlatformOwner: z.boolean().optional(),
    organizationType: z
        .object({
            id: z.number(),
            name: z.string(),
        })
        .optional()
        .nullable(),
    organizationSector: CompanySectorZodSchema.optional(),
    ingredientSuppliers: IngredientSupplierZodSchema.array().optional(),
    clientId: z.string().optional(),
    registrationNumber: z.string().optional(),
    externalId: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    zipcode: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    users: ContributorZodSchema.array().optional(),

    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),

    tags: z
        .union([
            z.record(z.string(), z.array(z.string())), // Format objet standard
            z.array(z.never()).length(0), // Tableau vide uniquement
        ])
        .optional()
        .transform((value): Record<string, string[]> | undefined => {
            // Convertir tableau vide en objet vide pour cohérence
            return (
                value === undefined ? undefined
                : Array.isArray(value) ? {}
                : value
            );
        }) as z.ZodType<Record<string, string[]> | undefined>,
    jsonAttributes: z
        .union([z.record(z.string(), z.any()), z.array(z.any())])
        .optional()
        .transform((val) => {
            // Si c'est un tableau, le convertir en objet vide pour compatibilité
            if (Array.isArray(val)) {
                return {};
            }
            return val;
        }),
});

export const CompanySchema = CompanyBaseSchema.extend({
    parents: z.array(CompanyBaseSchema).optional(),
});

export type Company = {
    "@id": IRI;
    id: number;
    name: string;
    organizationSector?: CompanySector;
    isProvider?: boolean;
    isPlatformOwner?: boolean;
    organizationType?: {
        id: number;
        name: string;
    } | null;
    ingredientSuppliers?: IngredientSupplier[];
    clientId?: string;
    registrationNumber?: string;
    externalId?: string;
    address1?: string;
    address2?: string;
    zipcode?: string;
    city?: string;
    state?: string;
    country?: string;
    phone?: string;
    email?: string;
    parents?: Company[];
    users?: Contributor[];
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    tags?: Record<string, string[]>; // Format object groupé par slug
    jsonAttributes?: Record<string, any>;
};
